import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { jwtDecode } from 'jwt-decode';

function RegistrationForm() {
    const { login } = useAuth(); // We might just manually set token/user
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        firstName: '', lastName: '', email: '', phoneNumber: '', password: '', state: '', city: '', profession: '', dateOfBirth: '',
        bloodGroup: '', allergies: '', medicalConditions: '', isOrganDonor: false, currentMedications: '',
        primaryCarePhysicianName: '', primaryCarePhysicianPhone: '', vaccinationRecords: '',
        emergencyContactName: '', emergencyContactRelation: '', emergencyContactPhone: ''
    });
    const [selectedFile, setSelectedFile] = useState(null);
    const [message, setMessage] = useState('');
    const [isError, setIsError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const fileInputRef = useRef(null);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: type === 'checkbox' ? (checked ? true : false) : value
        }));
    };

    const handleFileChange = (e) => {
        setSelectedFile(e.target.files[0]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage('');
        setIsError(false);

        // Use FormData to handle both text and file data
        const submissionData = new FormData();
        for (const key in formData) {
            submissionData.append(key, formData[key]);
        }
        if (selectedFile) {
            submissionData.append('profilePhoto', selectedFile);
        }

        try {
            const response = await fetch('https://healthcard.searchisend.com/health_api/register_user.php', {
                method: 'POST',
                // No 'Content-Type' header needed for FormData; browser sets it
                body: submissionData,
            });

            const result = await response.json();
            if (!response.ok) {
                throw new Error(result.message || 'An error occurred.');
            }

            setMessage(result.message);
            setIsError(false);

            // Log the user in with the newly generated token from registration
            if (result.token) {
                const decodedToken = jwtDecode(result.token);
                localStorage.setItem('user', JSON.stringify(decodedToken.data));
                localStorage.setItem('token', result.token);
                // Ideally trigger context update, but a reload/redirect works too
                setTimeout(() => {
                    navigate('/checkout');
                    window.location.reload(); // Force context update if needed
                }, 1000);
            } else {
                // Fallback if no token (shouldn't happen with updated API)
                setTimeout(() => {
                    navigate('/login');
                }, 2000);
            }

            // Reset form on success
            setFormData({
                firstName: '', lastName: '', email: '', phoneNumber: '', password: '', state: '', city: '', profession: '', dateOfBirth: '',
                bloodGroup: '', allergies: '', medicalConditions: '', isOrganDonor: false, currentMedications: '',
                primaryCarePhysicianName: '', primaryCarePhysicianPhone: '', vaccinationRecords: '',
                emergencyContactName: '', emergencyContactRelation: '', emergencyContactPhone: ''
            });
            setSelectedFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } catch (error) {
            setMessage(error.message);
            setIsError(true);
        } finally {
            setIsLoading(false);
        }
    };

    const inputClass = "w-full p-3.5 bg-white/60 text-brand-secondary rounded-xl border border-white/40 focus:outline-none focus:ring-2 focus:ring-brand-primary placeholder-slate-400 font-medium transition-shadow focus:bg-white";

    return (
        <div className="glass p-8 md:p-10 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] w-full max-w-3xl relative">
            <h2 className="text-4xl font-extrabold text-brand-secondary text-center mb-8 tracking-tight">Create Your Health Card</h2>
            <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                        <label className="block text-sm font-semibold text-brand-secondary mb-1.5 ml-1">First Name *</label>
                        <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="First Name" required className={inputClass} />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-brand-secondary mb-1.5 ml-1">Last Name *</label>
                        <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Last Name" required className={inputClass} />
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-semibold text-brand-secondary mb-1.5 ml-1">Email Address *</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Email Address" required className={inputClass} />
                </div>
                <div>
                    <label className="block text-sm font-semibold text-brand-secondary mb-1.5 ml-1">Phone Number *</label>
                    <input type="tel" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} placeholder="Phone Number" required className={inputClass} />
                </div>
                <div>
                    <label className="block text-sm font-semibold text-brand-secondary mb-1.5 ml-1">Password *</label>
                    <input type="password" name="password" value={formData.password} onChange={handleChange} placeholder="Password" required className={inputClass} />
                </div>
                <div className="bg-white/40 p-4 rounded-xl border border-white/60">
                    <label className="block text-sm font-semibold text-brand-secondary mb-2">Profile Picture (JPG/PNG, max 2MB)</label>
                    <input type="file" name="profilePhoto" onChange={handleFileChange} ref={fileInputRef} accept="image/jpeg, image/png" className="block w-full text-sm text-slate-500 file:mr-4 file:py-2.5 file:px-5 file:rounded-full file:border-0 file:text-sm file:font-bold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 transition-colors" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                        <label className="block text-sm font-semibold text-brand-secondary mb-1.5 ml-1">State *</label>
                        <input type="text" name="state" value={formData.state} onChange={handleChange} placeholder="State" required className={inputClass} />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-brand-secondary mb-1.5 ml-1">City *</label>
                        <input type="text" name="city" value={formData.city} onChange={handleChange} placeholder="City" required className={inputClass} />
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-semibold text-brand-secondary mb-1.5 ml-1">Profession / Job Title (Optional)</label>
                    <input type="text" name="profession" value={formData.profession} onChange={handleChange} placeholder="Profession / Job Title (Optional)" className={inputClass} />
                </div>

                <div className="my-8 border-t border-slate-200/60 pt-6">
                    <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-red-600">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path></svg>
                        </div>
                        <h3 className="text-2xl font-bold text-brand-secondary">Health & Emergency Info</h3>
                    </div>

                    <div className="space-y-5">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            <div>
                                <label className="block text-sm font-semibold text-brand-secondary mb-1.5 ml-1">Blood Group *</label>
                                <select name="bloodGroup" value={formData.bloodGroup} onChange={handleChange} required className={inputClass}>
                                    <option value="">Select Blood Group *</option>
                                    <option value="A+">A+</option><option value="A-">A-</option>
                                    <option value="B+">B+</option><option value="B-">B-</option>
                                    <option value="O+">O+</option><option value="O-">O-</option>
                                    <option value="AB+">AB+</option><option value="AB-">AB-</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-brand-secondary mb-1.5 ml-1">Allergies (if any)</label>
                                <input type="text" name="allergies" value={formData.allergies} onChange={handleChange} placeholder="Allergies (if any)" className={inputClass} />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-brand-secondary mb-1.5 ml-1">Date of Birth *</label>
                                <input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleChange} required className={`${inputClass} !py-2.5 w-full`} />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-brand-secondary mb-1.5 ml-1">Medical Conditions (if any)</label>
                            <textarea name="medicalConditions" value={formData.medicalConditions} onChange={handleChange} placeholder="Medical Conditions (if any)" rows="2" className={inputClass}></textarea>
                        </div>

                        <div className="bg-white/40 p-4 rounded-xl border border-white/60 flex items-center gap-3">
                            <input type="checkbox" id="isOrganDonor" name="isOrganDonor" checked={formData.isOrganDonor} onChange={handleChange} className="w-5 h-5 text-brand-primary rounded border-slate-300 focus:ring-brand-primary" />
                            <label htmlFor="isOrganDonor" className="text-brand-secondary font-semibold">I am a registered Organ Donor</label>
                            <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd"></path></svg>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-brand-secondary mb-1.5 ml-1">Current Medications & Dosages</label>
                            <textarea name="currentMedications" value={formData.currentMedications} onChange={handleChange} placeholder="Current Medications & Dosages" rows="2" className={inputClass}></textarea>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className="block text-sm font-semibold text-brand-secondary mb-1.5 ml-1">Primary Care Physician Name</label>
                                <input type="text" name="primaryCarePhysicianName" value={formData.primaryCarePhysicianName} onChange={handleChange} placeholder="Primary Care Physician (Name)" className={inputClass} />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-brand-secondary mb-1.5 ml-1">Physician Phone Number</label>
                                <input type="tel" name="primaryCarePhysicianPhone" value={formData.primaryCarePhysicianPhone} onChange={handleChange} placeholder="Physician Phone Number" className={inputClass} />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-brand-secondary mb-1.5 ml-1">Major Vaccinations (e.g., COVID-19, Tetanus)</label>
                            <input type="text" name="vaccinationRecords" value={formData.vaccinationRecords} onChange={handleChange} placeholder="Major Vaccinations (e.g., COVID-19, Tetanus)" className={inputClass} />
                        </div>

                        <div className="pt-4 border-t border-white/60">
                            <h4 className="text-lg font-bold text-brand-secondary mb-3">Emergency Contacts</h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                <div>
                                    <label className="block text-sm font-semibold text-brand-secondary mb-1.5 ml-1">Emergency Contact Name *</label>
                                    <input type="text" name="emergencyContactName" value={formData.emergencyContactName} onChange={handleChange} placeholder="Emergency Contact Name *" required className={inputClass} />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-brand-secondary mb-1.5 ml-1">Relation (e.g., Spouse) *</label>
                                    <input type="text" name="emergencyContactRelation" value={formData.emergencyContactRelation} onChange={handleChange} placeholder="Relation *" required className={inputClass} />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-brand-secondary mb-1.5 ml-1">Emergency Phone Number *</label>
                                    <input type="tel" name="emergencyContactPhone" value={formData.emergencyContactPhone} onChange={handleChange} placeholder="Emergency Phone *" required className={inputClass} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <button type="submit" disabled={isLoading} className="w-full p-4 mt-4 bg-brand-primary hover:bg-emerald-700 rounded-full font-bold text-white transition-all transform hover:-translate-y-1 shadow-lg shadow-emerald-700/30 disabled:bg-slate-300 disabled:transform-none disabled:shadow-none text-lg">
                    {isLoading ? 'Submitting...' : 'Register as Member'}
                </button>
            </form>
            {message && (
                <div className={`mt-6 text-center p-4 rounded-xl font-medium border ${isError ? 'bg-red-50 text-red-700 border-red-200' : 'bg-green-50 text-green-700 border-green-200'}`}>
                    {message}
                </div>
            )}
            <div className="mt-6 text-center text-brand-secondary font-medium">
                Already have an account? <span onClick={() => navigate('/login')} className="text-brand-primary cursor-pointer hover:underline font-bold">Login here</span>
            </div>
        </div>
    );
}

export default RegistrationForm;

